import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Modal } from '@components/ui/Modal';

import type { CreateSpecialtyRequest } from '../types';

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

interface AddSpecialtyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSpecialtyRequest) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}

export function AddSpecialtyModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
}: AddSpecialtyModalProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('specialties.modal.nameRequired'))
          .max(100, t('specialties.modal.nameTooLong')),
        description: z.string().max(1000).optional(),
        color: z
          .string()
          .optional()
          .refine((v) => !v || HEX_COLOR_RE.test(v), {
            message: t('specialties.modal.invalidColor'),
          }),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  function onFormSubmit(values: FormValues) {
    onSubmit({
      name: values.name,
      description: values.description || undefined,
      color: values.color || undefined,
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('specialties.modal.title')}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('specialties.modal.cancel')}
          </Button>
          <Button type="submit" form="add-specialty-form" isLoading={isSubmitting}>
            {t('specialties.modal.submit')}
          </Button>
        </>
      }
    >
      {errorMessage && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}
      <form
        id="add-specialty-form"
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
        className="space-y-4"
      >
        <Input
          label={t('specialties.modal.nameLabel')}
          placeholder={t('specialties.modal.namePlaceholder')}
          error={errors.name?.message}
          autoFocus
          {...register('name')}
        />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('specialties.modal.descriptionLabel')}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {t('specialties.modal.optional')}
            </span>
          </label>
          <textarea
            placeholder={t('specialties.modal.descriptionPlaceholder')}
            rows={3}
            className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            {...register('description')}
          />
        </div>
        <Input
          label={t('specialties.modal.colorLabel')}
          placeholder={t('specialties.modal.colorPlaceholder')}
          error={errors.color?.message}
          helperText={t('specialties.modal.colorHint')}
          {...register('color')}
        />
      </form>
    </Modal>
  );
}
